import React from "react";
import { Alert, Button } from "reactstrap";
import { connect } from "react-redux";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import SocketEngine from "../../core/SocketEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import ButtonLoader from "../../components/ButtonLoader/ButtonLoader";
import { Scopes } from "../../shared/scopes.enum";
import WorkOrderItems from "./WorkOrderItems";
import {
  ApproveWorkModal,
  AssigneeModal,
  CollectionModal,
  ConfirmModal,
  DeliveryModal,
  ItemFormModal,
  MessageDrawer,
  ServicePickerModal,
  ServicesCostModal,
} from "./WorkOrderDialogs";
import {
  ItemType,
  PaymentStatus,
  STAGES,
  WorkOrderStatus,
  canChangeDelivery,
  canQuote,
  isTerminal,
  deliveryUrgencyOf,
  deliveryUrgencyTone,
  DeliveryUrgency,
  formatDeliveryAt,
  fromLocalInputValue,
  money,
  currencySymbolOf,
  partsOpen,
  stageIndexOf,
  statusIcon,
  statusLabel,
  statusTone,
  tasksProgress,
} from "../../shared/work-orders";
import "../../assets/css/work-orders.css";

/**
 * The draft key for the client's own thread. Item drafts are keyed by item id,
 * which is a uuid, so this can never collide with one.
 */
const WO_THREAD = "work-order";

/**
 * The garage's side of one inspection report, run the way the garage app runs
 * it for an Owner or a Manager: price the findings the mechanics reported,
 * send them for the client to review, quote the work with a promised date,
 * approve the accepted job for the mechanics with its parts list, then — once
 * the mechanics have done their tasks — do the final check and hand the car
 * back. Starting the work and ticking parts and tasks off are the mechanics'
 * own steps in the app; here they are followed, not taken.
 *
 * Only one thing can be done at a time, and it is the thing the stage calls
 * for — the button under the report is whatever comes next, the same single
 * action the app puts at the bottom of the screen.
 *
 * The client works the other half of this on their phone. Both sides watch
 * `work_order:updated`, so an item approved in the app lands here without a
 * refresh, and every step taken here reaches the client the same way (the
 * backend raises it from the service, whoever moved the order).
 */
class WorkOrderManage extends CoreEngine {
  constructor(props) {
    super(props);
    const routeState = props.location?.state ?? {};
    this.state = {
      isLoading: true,
      workOrder: routeState.workOrder?.status ? routeState.workOrder : null,
      booking: routeState.booking ?? null,
      meta: routeState.meta ?? null,
      error: "",
      // Which action area a message belongs to. A banner pinned to the top of
      // a long page is invisible to someone who pressed a button at the bottom
      // of it, so the answer is rendered where the question was asked.
      messageAt: "actions",
      success: "",
      acting: null,
      note: "",
      noteDirty: false,
      savingNote: false,
      // itemId -> "photos"; the only panel that still opens inside a row
      openPanels: {},
      // Which thread the message drawer is showing: null when it is closed,
      // otherwise { id, name } — a null id being the client's own thread.
      thread: null,
      drafts: {},
      workers: [],
      // how a car can leave, loaded when it is handed over
      deliveryMethods: [],
      // The garage's services with their sub-services; null until loaded.
      branchServices: null,
      schedule: null,
      dialog: null,
      dialogError: "",
      itemDraft: null,
      assigneeCtx: null,
      deliveryCtx: null,
      confirmCtx: null,
      // A quote is a price, a date *and* an owner, asked for in that order.
      // The first two wait here while the next is picked, and all three go up
      // in one call.
      pendingServicesCost: null,
      pendingDeliveryAt: null,
    };
    this.engine = new RequestEngine();
    this.feedbackRef = React.createRef();
  }

  componentDidMount() {
    this.load();
    SocketEngine.on("work_order:updated", this.handleRemoteChange);
  }

  componentWillUnmount() {
    SocketEngine.off("work_order:updated", this.handleRemoteChange);
  }

  // ── data ──────────────────────────────────────────────────────────────────

  woId = () =>
    this.state.workOrder?.id ?? this.props.match?.params?.workOrderId;

  path = (suffix = "") => `work-orders/${this.woId()}${suffix}`;

  load = async () => {
    const id = this.woId();
    if (!id) {
      this.setState({ isLoading: false, error: strings.work_order_not_found });
      return;
    }
    try {
      const res = await this.engine.getItem(this.path());
      this.applyWorkOrder(res.data);
    } catch (e) {
      this.setState({ isLoading: false, error: strings.work_order_not_found });
    }
    this.props.loadingAction(false);
  };

  /**
   * Silent reload, used after an action and by the socket. Deliberately keeps
   * a note the user is part way through typing: the client approving an item
   * must not wipe what is half written here.
   */
  refresh = async () => {
    try {
      const res = await this.engine.getItem(this.path());
      this.applyWorkOrder(res.data);
    } catch (e) {
      // A failed background refresh leaves the last good copy on screen.
    }
  };

  applyWorkOrder = (workOrder) => {
    const live = this.liveItemIds(workOrder);
    this.setState((state) => ({
      workOrder,
      isLoading: false,
      note: state.noteDirty ? state.note : workOrder.note ?? "",
      // Drafts and open panels are keyed by item id: an item the garage has
      // since removed would keep an orphan open. The client's own thread is
      // not an item, so its draft is never pruned.
      drafts: this.pruneByItem(state.drafts, live, [WO_THREAD]),
      openPanels: this.pruneByItem(state.openPanels, live),
      // Reading a thread on an item that has just been removed elsewhere.
      thread:
        state.thread?.id && !live.has(state.thread.id) ? null : state.thread,
    }));
  };

  liveItemIds = (workOrder) => {
    const live = new Set();
    const walk = (items) =>
      (items ?? []).forEach((item) => {
        live.add(item.id);
        walk(item.children);
      });
    walk(workOrder.items);
    return live;
  };

  pruneByItem = (map, live, keep = []) =>
    Object.keys(map)
      .filter((id) => live.has(id) || keep.includes(id))
      .reduce((acc, id) => ({ ...acc, [id]: map[id] }), {});

  /** True when a branch-wide broadcast is about the order on screen. */
  isForThisOrder = (payload) => {
    if (!payload) return false;
    if (payload.workOrderId) return payload.workOrderId === this.woId();
    const { booking, workOrder } = this.state;
    if (payload.bookingId && booking?.id) return payload.bookingId === booking.id;
    if (payload.jobCardId && workOrder?.jobCardId) {
      return payload.jobCardId === workOrder.jobCardId;
    }
    return false;
  };

  handleRemoteChange = (payload) => {
    if (!this.isForThisOrder(payload)) return;
    // Mid-action, this is the echo of our own change — the action reloads when
    // it finishes, so refreshing here would only race with it.
    if (this.state.acting) return;
    this.refresh();
  };

  /**
   * Runs one step of the flow: clears the banners, keeps a single action in
   * flight, reloads on success and surfaces whatever the backend refused with
   * (its wording is the wording the app shows, so both sides read alike).
   *
   * `key` names the step, so only the button that started it shows a spinner.
   * Every other action is still locked out while it runs — that is `disabled`,
   * which is a different question from which button you are waiting on.
   */
  act = async (run, success, key = "action") => {
    this.setState({ acting: key, error: "", success: "", messageAt: "actions" });
    try {
      const res = await run();
      const status = res?.status;
      if (![200, 201, 204].includes(status)) {
        throw new Error(res?.data?.message || strings.error);
      }
      await this.refresh();
      this.setState({
        acting: null,
        success,
        dialog: null,
        dialogError: "",
        itemDraft: null,
        assigneeCtx: null,
        deliveryCtx: null,
        confirmCtx: null,
        pendingDeliveryAt: null,
      });
      return true;
    } catch (e) {
      this.setState({ acting: null, error: this.messageOf(e) });
      return false;
    }
  };

  messageOf = (error) => {
    const body = error?.response?.data?.message ?? error?.message;
    return Array.isArray(body) ? body.join(", ") : body || strings.error;
  };

  // ── note ──────────────────────────────────────────────────────────────────

  saveNote = async () => {
    const { workOrder, note } = this.state;
    this.setState({ savingNote: true, error: "", success: "", messageAt: "note" });
    try {
      await this.engine.putPath(this.path(), {
        version: workOrder.version,
        note,
      });
      this.setState({
        savingNote: false,
        noteDirty: false,
        success: strings.note_saved,
        messageAt: "note",
      });
      this.refresh();
    } catch (e) {
      this.setState({
        savingNote: false,
        error: this.messageOf(e),
        messageAt: "note",
      });
    }
  };

  // ── items ─────────────────────────────────────────────────────────────────

  openItemForm = (draft) => {
    this.setState({ dialog: "item", itemDraft: draft, dialogError: "" });
  };

  submitItem = async (values) => {
    const { itemDraft } = this.state;
    const { item, parentId, isOption } = itemDraft;
    const payload = {
      name: values.name,
      description: values.description || undefined,
      estimatedCost: values.estimatedCost ?? undefined,
      costPrice: values.costPrice ?? undefined,
      // a task's hours and warranty go together; null clears one
      ...values.task,
    };

    if (item) {
      await this.act(
        () =>
          this.engine.updateItem(this.path("/items"), item.id, {
            ...payload,
            // `payload.estimatedCost` collapses an emptied field's `null`
            // down to `undefined` (above), which the API reads as "cost
            // wasn't touched" rather than "cost was removed" — so clearing
            // it has to be said explicitly, or the item's old price sticks.
            clearEstimatedCost: values.estimatedCost == null,
            // the same goes for the cost price
            clearCostPrice: values.costPrice == null,
            version: item.version,
            type: values.type,
          }),
        strings.item_updated
      );
      return;
    }

    // A choice option is an ordinary child of a parent that has been switched
    // to "the client picks one" — the switch happens first, so the option is
    // never briefly rendered as a plain sub-item.
    if (isOption && itemDraft.parent?.selectionMode !== "one") {
      try {
        await this.engine.updateItem(this.path("/items"), parentId, {
          version: itemDraft.parent.version,
          selectionMode: "one",
        });
      } catch (e) {
        this.setState({ dialogError: this.messageOf(e) });
        return;
      }
    }

    await this.act(
      () =>
        this.engine.saveItem(this.path("/items"), {
          ...payload,
          type: values.type,
          parentId: parentId || undefined,
        }),
      strings.item_added
    );
  };

  openServicePicker = () => {
    this.setState({ dialog: "pick_services", dialogError: "" });
    this.loadBranchServices();
  };

  loadBranchServices = async () => {
    if (this.state.branchServices) return;
    try {
      const res = await this.engine.getItem(this.path("/branch-services"));
      this.setState({ branchServices: res.data ?? [] });
    } catch (e) {
      this.setState({ branchServices: [], dialogError: this.messageOf(e) });
    }
  };

  // One item per ticked sub-service, name and price copied from it. Stops at
  // the first refusal so the message names what went wrong.
  submitServicePick = ({ details, type }) =>
    this.act(async () => {
      let res;
      for (const detail of details) {
        res = await this.engine.saveItem(this.path("/items"), {
          name: detail.name,
          // The api only takes a positive cost; an unpriced or free
          // sub-service goes in without one.
          estimatedCost: detail.price > 0 ? Number(detail.price) : undefined,
          type,
          serviceDetailId: detail.id,
        });
        if (![200, 201].includes(res?.status)) return res;
      }
      return res;
    }, strings.items_added);

  removeItem = (item) =>
    this.setState({
      dialog: "confirm",
      confirmCtx: {
        title: strings.remove_item,
        body: strings.confirm_remove_item,
        confirmLabel: strings.delete,
        color: "danger",
        run: () =>
          this.act(
            () => this.engine.deleteItem(this.path("/items"), item.id),
            strings.item_removed
          ),
      },
    });

  togglePanel = (itemId, panel) =>
    this.setState((state) => ({
      openPanels: {
        ...state.openPanels,
        [itemId]: state.openPanels[itemId] === panel ? null : panel,
      },
    }));

  setDraft = (itemId, value) =>
    this.setState((state) => ({
      drafts: { ...state.drafts, [itemId]: value },
    }));

  // ── conversations ─────────────────────────────────────────────────────────

  /** `item` is null for the client's own thread on the whole order. */
  openThread = (item) =>
    this.setState({
      thread: item ? { id: item.id, name: item.name } : { id: null, name: null },
    });

  closeThread = () => this.setState({ thread: null });

  /** The thread being read, resolved against the copy just loaded. */
  threadOf = (workOrder) => {
    const { thread } = this.state;
    if (!thread) return null;
    if (!thread.id) {
      return {
        key: WO_THREAD,
        title: strings.message_client,
        subtitle: null,
        messages: workOrder.messages ?? [],
      };
    }
    const item = this.findItem(workOrder.items, thread.id);
    return {
      key: thread.id,
      // The name comes off the live copy so a rename shows up here too.
      title: item?.name ?? thread.name,
      subtitle: strings.about_this_item,
      messages: item?.messages ?? [],
    };
  };

  findItem = (items, id) => {
    for (const item of items ?? []) {
      if (item.id === id) return item;
      const found = this.findItem(item.children, id);
      if (found) return found;
    }
    return null;
  };

  /** Sends whatever is in the composer to whichever thread is open. */
  sendThreadMessage = async () => {
    const { thread } = this.state;
    if (!thread) return;
    const key = thread.id ?? WO_THREAD;
    const body = (this.state.drafts[key] || "").trim();
    if (!body) return;

    const path = thread.id
      ? this.path(`/items/${thread.id}/messages`)
      : this.path("/messages");
    // The drawer stays open — a reply belongs to a conversation, and `act`
    // only closes dialogs.
    const sent = await this.act(
      () => this.engine.saveItem(path, { body }),
      strings.reply_sent
    );
    if (sent) this.setDraft(key, "");
  };

  uploadPhoto = async (item, kind, file) => {
    const form = new FormData();
    form.append("file", file);
    await this.act(
      () =>
        this.engine.postItemMultipart(
          this.path(`/items/${item.id}/photos?kind=${kind}`),
          form
        ),
      strings.photo_uploaded
    );
  };

  removePhoto = (item, photo) =>
    this.setState({
      dialog: "confirm",
      confirmCtx: {
        title: strings.item_photos,
        body: strings.confirm_remove_photo,
        confirmLabel: strings.delete,
        color: "danger",
        run: () =>
          this.act(
            () =>
              this.engine.deleteItem(
                this.path(`/items/${item.id}/photos`),
                photo.id
              ),
            strings.photo_removed
          ),
      },
    });

  // ── the run ───────────────────────────────────────────────────────────────

  confirmThen = (ctx) => this.setState({ dialog: "confirm", confirmCtx: ctx });

  /**
   * Leaf items (sub-items, and items with none) that have no price yet,
   * labelled "Parent › Child" so they can be found.
   */
  unpricedItems = () => {
    const out = [];
    const walk = (items, parentName) =>
      (items ?? []).forEach((item) => {
        if (item.children?.length) {
          walk(item.children, item.name);
          return;
        }
        if (!(Number(item.estimatedCost) > 0)) {
          out.push(parentName ? `${parentName} › ${item.name}` : item.name);
        }
      });
    walk(this.state.workOrder?.items);
    return out;
  };

  sendToClient = () => {
    const unpriced = this.unpricedItems();
    this.confirmThen({
      title: unpriced.length
        ? strings.unpriced_items_title
        : strings.send_to_customer,
      body: unpriced.length ? (
        <>
          <p className="mb-2">
            {strings.formatString(strings.unpriced_items_message, unpriced.length)}
          </p>
          <ul className="mb-2">
            {unpriced.map((name, i) => (
              <li key={i}>
                <strong>{name}</strong>
              </li>
            ))}
          </ul>
          <p className="mb-0 text-muted">{strings.unpriced_items_hint}</p>
        </>
      ) : (
        strings.confirm_send_to_customer
      ),
      color: unpriced.length ? "warning" : undefined,
      confirmLabel: unpriced.length ? strings.send_anyway : strings.send,
      run: () =>
        this.act(
          () => this.engine.patchItem(this.path("/send")),
          strings.work_order_sent,
          "send"
        ),
    });
  };

  resendForReview = () =>
    this.confirmThen({
      title: strings.resend_for_review,
      body: strings.confirm_resend,
      confirmLabel: strings.send,
      run: () =>
        this.act(
          () => this.engine.patchItem(this.path("/resend")),
          strings.resent_for_review,
          "resend"
        ),
    });

  /**
   * The other way out of a sent quote: pull it back before the customer acts
   * on it so the price, date or owner can be corrected and sent again.
   */
  recallQuote = () =>
    this.confirmThen({
      title: strings.recall_quote,
      body: strings.confirm_recall_quote,
      confirmLabel: strings.send,
      run: () =>
        this.act(
          () => this.engine.patchItem(this.path("/recall-quote")),
          strings.quote_recalled,
          "recall"
        ),
    });

  /**
   * The quote prices the work the garage will carry out, and that price is
   * asked for first: an order can't be quoted without it, so the worker is
   * stopped here rather than after a date and an owner have been picked.
   */
  startFinalQuote = () =>
    this.setState({
      dialog: "services",
      pendingServicesCost: null,
      dialogError: "",
    });

  submitServicesCost = async (servicesCost) => {
    // The schedule is fetched before the next dialog can open, so the button
    // holds the wait itself rather than looking idle and taking a second click.
    this.setState({ acting: "quote" });
    await this.loadSchedule();
    this.setState({
      acting: null,
      pendingServicesCost: servicesCost,
      dialog: "delivery",
      deliveryCtx: { mode: "quote" },
      dialogError: "",
    });
  };

  submitDelivery = async (localValue) => {
    const deliveryAt = fromLocalInputValue(localValue);
    if (this.state.deliveryCtx?.mode === "change") {
      await this.act(
        () => this.engine.patchItem(this.path("/delivery"), { deliveryAt }),
        strings.delivery_updated
      );
      return;
    }
    // Quoting: the owner comes next, then both go up together.
    await this.loadWorkers();
    this.setState({
      pendingDeliveryAt: deliveryAt,
      dialog: "assignee",
      assigneeCtx: { allowLater: true, mode: "quote" },
      dialogError: "",
    });
  };

  changeDelivery = async () => {
    await this.loadSchedule();
    this.setState({
      dialog: "delivery",
      deliveryCtx: { mode: "change" },
      dialogError: "",
    });
  };

  openAssignees = async (mode = "assign") => {
    await this.loadWorkers();
    this.setState({
      dialog: "assignee",
      assigneeCtx: { allowLater: false, mode },
      dialogError: "",
    });
  };

  submitAssignees = async (assigneeIds) => {
    const { assigneeCtx, pendingDeliveryAt, pendingServicesCost } = this.state;

    if (assigneeCtx?.mode === "quote") {
      await this.act(
        () =>
          this.engine.patchItem(this.path("/final-quote"), {
            deliveryAt: pendingDeliveryAt,
            servicesCost: pendingServicesCost,
            ...(assigneeIds.length > 0 && { assigneeIds }),
          }),
        strings.quote_sent
      );
      return;
    }

    const saved = await this.act(
      () => this.engine.patchItem(this.path("/assignee"), { assigneeIds }),
      strings.assignees_saved
    );
    // Assigned on the way to approving: carry on to the parts list.
    if (saved && assigneeCtx?.mode === "approve") this.openApproveWork();
  };

  /**
   * The customer accepted: approve the job for the mechanics with the parts
   * it needs. Approving again replaces the garage's own lines, so this is
   * also how the list is corrected before the work starts.
   */
  openApproveWork = () => {
    // The approved job goes to its mechanics, so it needs some first.
    if (!(this.state.workOrder?.assignees ?? []).length) {
      this.confirmThen({
        title: strings.assign_mechanics_first,
        body: strings.assign_mechanics_first_hint,
        confirmLabel: strings.assign_mechanics,
        run: () => this.openAssignees("approve"),
      });
      return;
    }
    this.setState({ dialog: "approve", dialogError: "" });
  };

  /**
   * Who the job goes to by default: whoever is already on it, or else the
   * mechanics who reported the findings, first reporter first.
   */
  defaultAssigneeIds = () => {
    const { workOrder } = this.state;
    const current = (workOrder?.assignees ?? []).map((one) => one.id);
    if (current.length) return current;
    const reported = [];
    const walk = (items) =>
      (items ?? []).forEach((item) => {
        if (item.authorId && item.reportedAt) reported.push(item);
        walk(item.children);
      });
    walk(workOrder?.items);
    reported.sort((a, b) =>
      String(a.reportedAt).localeCompare(String(b.reportedAt))
    );
    return [...new Set(reported.map((item) => item.authorId))];
  };

  submitApproveWork = (parts) =>
    this.act(
      () => this.engine.patchItem(this.path("/approve-work"), { parts }),
      strings.work_approved,
      "approve"
    );

  removePart = (part) =>
    this.confirmThen({
      title: strings.remove_part,
      body: strings.confirm_remove_part,
      confirmLabel: strings.delete,
      color: "danger",
      run: () =>
        this.act(
          () => this.engine.deleteItem(this.path("/parts"), part.id),
          strings.part_removed
        ),
    });

  /**
   * The mechanics have done every task: the Owner or a Manager checks the car,
   * and only then is the customer told it is ready.
   */
  markReadyToDeliver = () =>
    this.confirmThen({
      title: strings.final_check_ready,
      body: strings.confirm_ready_to_deliver,
      confirmLabel: strings.confirm,
      run: () =>
        this.act(
          () => this.engine.patchItem(this.path("/ready-to-deliver")),
          strings.marked_ready,
          "ready"
        ),
    });

  confirmCash = (andDeliver = false) =>
    this.confirmThen({
      title: strings.confirm_cash_collected,
      body: strings.formatString(
        strings.confirm_cash_collected_body,
        money(this.payment().netTotal, this.currencySymbol())
      ),
      confirmLabel: strings.confirm,
      color: "success",
      run: async () => {
        const paid = await this.act(
          () => this.engine.patchItem(this.path("/confirm-payment")),
          strings.payment_confirmed,
          "cash"
        );
        if (paid && andDeliver) this.deliver();
      },
    });

  /**
   * The car leaving the premises is the one step that refuses an open
   * invoice. Cash owed can be settled on the spot; anything else has to clear
   * on the client's side first.
   */
  handoverVehicle = () => {
    const payment = this.payment();
    if (payment.paymentStatus === PaymentStatus.PAID) {
      this.deliver();
      return;
    }
    if (payment.paymentStatus === PaymentStatus.AWAITING_CASH) {
      this.confirmCash(true);
      return;
    }
    this.setState({ error: strings.settle_invoice_first, messageAt: "actions" });
  };

  /**
   * Beside how it was paid for: how the car leaves. Asks for one of the
   * delivery methods — and the driver or recovery company when it calls for
   * one — exactly as the worker app's handover does.
   */
  deliver = async () => {
    await this.loadDeliveryMethods();
    this.setState({ dialog: "collection", dialogError: "" });
  };

  loadDeliveryMethods = async () => {
    if (this.state.deliveryMethods.length > 0) return;
    try {
      const res = await this.engine.getItem("delivery-methods?isActive=true");
      const data = res?.data;
      const list = Array.isArray(data) ? data : data?.items ?? [];
      this.setState({
        deliveryMethods: list.map((method) => ({
          value: method.id,
          label: method.name,
          details: method.details || "none",
        })),
      });
    } catch (e) {
      this.setState({ deliveryMethods: [] });
    }
  };

  /**
   * The car leaves and the order is finished. Nothing on this screen is worked
   * on after that, so the counter is put back on the dashboard, where the next
   * car of the day is waiting.
   */
  submitCollection = async (collection) => {
    const delivered = await this.act(
      () => this.engine.patchItem(this.path("/delivered"), collection),
      strings.vehicle_delivered,
      "deliver"
    );
    if (delivered) {
      this.props.history.push("/admin/dashboard");
    } else {
      // keep the refusal in the dialog the reader is looking at
      this.setState((state) => ({ dialogError: state.error }));
    }
  };

  loadWorkers = async () => {
    if (this.state.workers.length > 0) return;
    try {
      const res = await this.engine.getItem(this.path("/branch-workers"));
      this.setState({ workers: res.data ?? [] });
    } catch (e) {
      this.setState({ workers: [] });
    }
  };

  loadSchedule = async () => {
    if (this.state.schedule) return;
    try {
      const res = await this.engine.getItem(this.path("/branch-schedule"));
      this.setState({ schedule: res.data ?? null });
    } catch (e) {
      this.setState({ schedule: null });
    }
  };

  payment = () => this.state.workOrder?.payment ?? {};

  /**
   * What this order's amounts are shown in. Read off the order itself, which
   * carries the currency frozen when it was opened, so a garage that switches
   * currency later does not re-label work it has already billed.
   */
  currencySymbol = () => currencySymbolOf(this.state.workOrder);

  closeDialog = () =>
    this.setState({
      dialog: null,
      dialogError: "",
      itemDraft: null,
      assigneeCtx: null,
      deliveryCtx: null,
      confirmCtx: null,
      pendingServicesCost: null,
      pendingDeliveryAt: null,
    });

  // ── render ────────────────────────────────────────────────────────────────

  /**
   * The approved job's parts list: the garage's own lines and the ones the
   * mechanics asked for. The mechanics tick each off in the app as it is in
   * hand; Start Work waits for the last. Any line can be taken off here while
   * the list is open, as an Owner can in the app.
   */
  renderParts(workOrder, canWrite) {
    if (!workOrder.workApprovedAt) return null;
    const parts = workOrder.parts ?? [];
    const checked = parts.filter((part) => part.checked).length;
    const open = canWrite && partsOpen(workOrder.status);
    const taskNames = {};
    const walk = (items) =>
      (items ?? []).forEach((item) => {
        taskNames[item.id] = item.name;
        walk(item.children);
      });
    walk(workOrder.items);

    return (
      <div className="wo-card">
        <div className="wo-card__head">
          <h6 className="wo-card__title">
            <i className="nc-icon nc-box" />
            {strings.parts_list}
          </h6>
          {parts.length > 0 && (
            <span
              className={`wo-card__count${
                checked === parts.length ? " is-done" : ""
              }`}
            >
              {strings.formatString(strings.parts_progress, checked, parts.length)}
            </span>
          )}
        </div>
        {parts.length === 0 && (
          <div className="wo-hint">{strings.no_parts_needed}</div>
        )}
        {parts.map((part) => {
          const sub = [
            part.requestedByName &&
              strings.formatString(strings.requested_by, part.requestedByName),
            taskNames[part.itemId] &&
              strings.formatString(strings.for_task, taskNames[part.itemId]),
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <div
              key={part.id}
              className={`wo-part${part.checked ? " is-checked" : ""}`}
            >
              <i
                className={`wo-part__check nc-icon ${
                  part.checked ? "nc-check-2" : "nc-time-alarm"
                }`}
              />
              <span className="wo-part__text">
                <span className="wo-part__name">{part.name}</span>
                {sub && <span className="wo-part__sub">{sub}</span>}
              </span>
              <span className="wo-part__qty">× {part.quantity ?? 1}</span>
              {open && (
                <button
                  type="button"
                  className="wo-act is-danger"
                  title={strings.remove_part}
                  aria-label={strings.remove_part}
                  disabled={Boolean(this.state.acting)}
                  onClick={() => this.removePart(part)}
                >
                  <i className="nc-icon nc-simple-remove" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderStage(status) {
    const tone = statusTone(status);
    const current = stageIndexOf(status);

    return (
      <div className={`wo-stage is-${tone}`}>
        <span className="wo-stage__icon">
          <i className={statusIcon(status)} />
        </span>
        <span className="wo-stage__text">
          <span className="wo-stage__label">{statusLabel(status)}</span>
        </span>
        {/* Declined never reaches a step of the run, so it shows no rail. */}
        {status !== WorkOrderStatus.DECLINED && (
          <span className="wo-stage__rail">
            {STAGES.map((stage, index) => (
              <span
                key={stage}
                className={`wo-stage__step${
                  index < current ? " is-done" : ""
                }${index === current ? " is-current" : ""}`}
              />
            ))}
          </span>
        )}
      </div>
    );
  }

  renderDelivery(workOrder, canWrite) {
    const { deliveryAt, assignees = [] } = workOrder;
    if (!deliveryAt) return null;
    const urgency = deliveryUrgencyOf(deliveryAt);
    const tone = deliveryUrgencyTone(urgency);
    const names = assignees.map((one) => one.name).filter(Boolean).join(", ");
    const editable = canWrite && canChangeDelivery(workOrder.status);

    return (
      <div className={`wo-delivery is-${tone}`}>
        <span className="wo-delivery__icon">
          <i className="nc-icon nc-calendar-60" />
        </span>
        <span className="wo-delivery__text">
          <span className="wo-delivery__when">
            {formatDeliveryAt(deliveryAt)}
          </span>
          <span className="wo-delivery__who">
            {names
              ? strings.formatString(strings.delivery_promised_to, names)
              : strings.delivery_promised}
          </span>
        </span>
        {(urgency === DeliveryUrgency.OVERDUE ||
          urgency === DeliveryUrgency.TODAY) && (
          <span className={`wo-pill is-${tone}`}>
            {urgency === DeliveryUrgency.OVERDUE
              ? strings.delivery_overdue
              : strings.delivery_due_today}
          </span>
        )}
        {editable && (
          <>
            <button
              type="button"
              className="wo-link"
              onClick={this.changeDelivery}
            >
              <i className="nc-icon nc-calendar-60" /> {strings.change_date}
            </button>
            <button
              type="button"
              className="wo-link"
              onClick={() => this.openAssignees("assign")}
            >
              <i className="nc-icon nc-single-02" />{" "}
              {names ? strings.change : strings.assign}
            </button>
          </>
        )}
      </div>
    );
  }

  renderPayment() {
    const payment = this.payment();
    const symbol = this.currencySymbol();
    if (payment.netTotal === undefined || payment.netTotal === null) return null;

    return (
      <div className="wo-card">
        <div className="wo-card__head">
          <h6 className="wo-card__title">
            <i className="nc-icon nc-money-coins" />
            {strings.payment_summary}
          </h6>
        </div>
        <div className="wo-lines">
          <div className="wo-line">
            <span>{strings.parts_total}</span>
            <span className="wo-line__value">{money(payment.grossTotal, symbol)}</span>
          </div>
          <div className="wo-line">
            <span>{strings.labor_total}</span>
            <span className="wo-line__value">{money(payment.laborTotal, symbol)}</span>
          </div>
          {payment.hasVat && (
            <div className="wo-line">
              <span>
                {strings.vat}
                {payment.vatPercentage ? ` (${payment.vatPercentage}%)` : ""}
              </span>
              <span className="wo-line__value">{money(payment.vatAmount, symbol)}</span>
            </div>
          )}
          <div className="wo-line is-net">
            <span>{strings.net_total}</span>
            <span className="wo-line__value">{money(payment.netTotal, symbol)}</span>
          </div>
          <div className="wo-line">
            <span>{strings.payment_method}</span>
            <span className="wo-line__value">
              {payment.paymentMethod?.name ?? "—"}
            </span>
          </div>
          <div className="wo-line">
            <span>{strings.status}</span>
            <span className="wo-line__value">
              {payment.paymentStatus === PaymentStatus.PAID ? (
                <span className="wo-pill is-success">{strings.paid}</span>
              ) : payment.paymentStatus === PaymentStatus.AWAITING_CASH ? (
                <span className="wo-pill is-warning">{strings.payment_awaiting_cash}</span>
              ) : (
                <span className="wo-pill is-muted">{strings.payment_unpaid}</span>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  /**
   * The result of an action, rendered beside the button that caused it.
   *
   * These used to be one pair of banners at the very top of the page. On a
   * work order that scrolls — items, options, messages — pressing "Mark
   * delivered" at the bottom put the refusal somewhere the reader could not
   * see, so the button looked like it had simply done nothing.
   */
  renderFeedback(where) {
    const { error, success, messageAt } = this.state;
    if (messageAt !== where) return null;
    if (!error && !success) return null;

    return (
      <div className="wo-feedback" ref={this.feedbackRef}>
        {error && (
          <Alert color="danger" toggle={() => this.setState({ error: "" })}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" toggle={() => this.setState({ success: "" })}>
            {success}
          </Alert>
        )}
      </div>
    );
  }

  /**
   * Rendering next to the button is enough when the button is on screen, which
   * is the normal case since the reader just pressed it. This covers the rest:
   * "nearest" scrolls only when the message is actually out of view, so a
   * visible one never yanks the page around.
   */
  componentDidUpdate(_prevProps, prevState) {
    const changed =
      this.state.error !== prevState.error ||
      this.state.success !== prevState.success;
    if (!changed || (!this.state.error && !this.state.success)) return;
    const node = this.feedbackRef.current;
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  /**
   * "Arrived by: Driver, Sami, 050… · Collected by: Owner" — how the car came
   * and went, with whoever brought or took it, as the app shows it.
   */
  arrivalAndCollection(workOrder) {
    const join = (...parts) => parts.filter(Boolean).join(", ");
    return [
      workOrder.arrivalMethodName &&
        strings.formatString(
          strings.arrived_by,
          join(
            workOrder.arrivalMethodName,
            workOrder.arrivalContactName,
            workOrder.arrivalContactPhone
          )
        ),
      workOrder.collectionMethodName &&
        strings.formatString(
          strings.collected_by,
          join(
            workOrder.collectionMethodName,
            workOrder.collectionContactName,
            workOrder.collectionContactPhone
          )
        ),
    ]
      .filter(Boolean)
      .join(" · ");
  }

  /** One action, for whatever the stage calls for next. */
  renderActions(workOrder, canWrite) {
    if (!canWrite) return null;
    const { status, items = [] } = workOrder;
    const { acting } = this.state;
    // One step at a time, so every action is locked while any of them runs.
    // The spinner is the narrower question — it belongs to the one step being
    // waited on, not to every button on the screen.
    const busy = Boolean(acting);
    const payment = this.payment();
    const cashDue = payment.paymentStatus === PaymentStatus.AWAITING_CASH;

    const wait = (text, tone = "", hint = "", icon = "nc-icon nc-watch-time") => (
      <div className={`wo-wait${tone ? ` is-${tone}` : ""}`}>
        <i className={icon} />
        <span className="wo-wait__text">
          <span>{text}</span>
          {hint && <span className="wo-wait__hint">{hint}</span>}
        </span>
      </div>
    );
    const progress = tasksProgress(items);

    return (
      <div className="wo-actions">
        {this.renderFeedback("actions")}

        {status === WorkOrderStatus.DRAFT && (
          <ButtonLoader
            color="primary"
            loading={acting === "send"}
            disabled={busy || items.length === 0}
            onClick={this.sendToClient}
          >
            {strings.send_to_customer}
          </ButtonLoader>
        )}

        {status === WorkOrderStatus.SENT && (
          <>
            {wait(strings.waiting_client_review)}
            {/* Items stay editable while the customer is reviewing — this
                nudges them with a fresh notification after a change instead
                of relying on them to notice on their own. */}
            <Button
              color="default"
              outline
              disabled={busy}
              onClick={this.resendForReview}
            >
              {strings.resend_for_review}
            </Button>
          </>
        )}

        {canQuote(status) && (
          <>
            <ButtonLoader
              color="primary"
              loading={acting === "quote"}
              disabled={busy}
              onClick={this.startFinalQuote}
            >
              {strings.send_final_quote}
            </ButtonLoader>
            {/* The other way out of a finished review: hand it back so the
                client decides again from scratch. */}
            <Button
              color="default"
              outline
              disabled={busy}
              onClick={this.resendForReview}
            >
              {strings.resend_for_review}
            </Button>
          </>
        )}

        {status === WorkOrderStatus.QUOTED && (
          <>
            {wait(strings.waiting_client_accept)}
            {/* The quote hasn't been accepted yet — it can still be pulled
                back to fix a wrong price, date or assignee before resending. */}
            <Button
              color="default"
              outline
              disabled={busy}
              onClick={this.recallQuote}
            >
              {strings.recall_quote}
            </Button>
          </>
        )}

        {/* Accepted: the job is approved for the mechanics with its parts
            list; the mechanics tick the parts off and start it in the app. */}
        {status === WorkOrderStatus.ACCEPTED && !workOrder.workApprovedAt && (
          <ButtonLoader
            color="info"
            loading={acting === "approve"}
            disabled={busy}
            onClick={this.openApproveWork}
          >
            {strings.approve_for_work}
          </ButtonLoader>
        )}

        {status === WorkOrderStatus.ACCEPTED && workOrder.workApprovedAt && (
          <>
            {wait(
              strings.waiting_for_mechanic_start,
              "primary",
              "",
              "nc-icon nc-settings-tool-66"
            )}
            {/* the list can still be corrected before the work starts */}
            <Button
              color="default"
              outline
              disabled={busy}
              onClick={this.openApproveWork}
            >
              {strings.edit_parts_list}
            </Button>
          </>
        )}

        {/* In progress: each mechanic marks their own tasks done; the last
            one finishes the job and calls the garage for the final check. */}
        {status === WorkOrderStatus.IN_PROGRESS &&
          progress &&
          wait(
            strings.formatString(
              strings.tasks_progress,
              progress.done,
              progress.total
            ),
            "primary",
            strings.tasks_progress_hint,
            "nc-icon nc-settings-tool-66"
          )}

        {status === WorkOrderStatus.DONE && (
          <>
            {wait(strings.waiting_final_check, "sky", "", "nc-icon nc-zoom-split")}
            <ButtonLoader
              color="info"
              loading={acting === "ready"}
              disabled={busy}
              onClick={this.markReadyToDeliver}
            >
              {strings.final_check_ready}
            </ButtonLoader>
          </>
        )}

        {status === WorkOrderStatus.READY_TO_DELIVER && (
          <>
            {wait(strings.awaiting_pickup, "sky")}
            <ButtonLoader
              color="success"
              loading={acting === "deliver"}
              disabled={busy}
              onClick={this.handoverVehicle}
            >
              {strings.mark_delivered}
            </ButtonLoader>
          </>
        )}

        {status === WorkOrderStatus.DELIVERED &&
          wait(
            strings.work_order_delivered_note,
            "success",
            this.arrivalAndCollection(workOrder)
          )}
        {status === WorkOrderStatus.DECLINED &&
          wait(strings.work_order_declined_note, "danger")}

        {/* Cash sits alongside the job's own state — it can be collected any
            time between acceptance and handover — so it isn't keyed on the
            status. */}
        {cashDue && (
          <div className="wo-cash">
            <span className="wo-cash__text">
              <span className="wo-cash__title">
                {strings.payment_awaiting_cash} ·{" "}
                {money(payment.netTotal, this.currencySymbol())}
              </span>
              <span className="wo-cash__hint">{strings.cash_due_hint}</span>
            </span>
            <ButtonLoader
              color="success"
              loading={acting === "cash"}
              disabled={busy}
              onClick={() => this.confirmCash(false)}
            >
              {strings.confirm_cash_collected}
            </ButtonLoader>
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      workOrder,
      booking,
      meta,
      isLoading,
      error,
      note,
      savingNote,
      openPanels,
      drafts,
      acting,
      dialog,
      dialogError,
      itemDraft,
      assigneeCtx,
      deliveryCtx,
      confirmCtx,
      workers,
      schedule,
    } = this.state;

    const canWrite = this.hasPermission([
      Scopes.WORK_ORDERS_WRITE,
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_BOOKINGS_WRITE,
    ]);

    if (isLoading) {
      return (
        <div className="content">
          <p>{strings.loading}…</p>
        </div>
      );
    }

    if (!workOrder) {
      return (
        <div className="content">
          <Alert color="danger">{error || strings.work_order_not_found}</Alert>
        </div>
      );
    }

    const status = workOrder.status;
    const messages = workOrder.messages ?? [];
    // Unlike items, the note isn't part of what the client is quoted on, so
    // it stays open for edits through the whole run — only handover or a
    // decline, which also closes the job card, locks it.
    const noteEditable = canWrite && !isTerminal(status);
    const thread = this.threadOf(workOrder);

    return (
      <div className="content wo">
        {/* Read-only visitors get no action panel for a message to sit
            beside, so theirs stays at the top. */}
        {!canWrite && this.renderFeedback("actions")}

        <div className="wo-head">
          <div className="wo-head__main">
            <h5 className="wo-head__title">
              {strings.work_order}
              {!workOrder.bookingId && !booking && (
                <span className="wo-pill is-muted">{strings.walk_in}</span>
              )}
            </h5>
            <div className="wo-head__meta">
              {meta?.customer}
              {meta?.vehicle && meta.vehicle !== "—" && ` — ${meta.vehicle}`}
            </div>
          </div>

          {/* The stage reads beside the title rather than in a band of its
              own — it is one word and a progress rail. */}
          {this.renderStage(status)}

          <div className="wo-head__actions">
            <Button
              color="default"
              size="sm"
              onClick={() => this.props.history.goBack()}
            >
              ← {strings.back}
            </Button>
            <Button
              color="default"
              size="sm"
              onClick={() => this.openThread(null)}
            >
              <i className="nc-icon nc-chat-round" /> {strings.message_client}
              {messages.length > 0 && (
                <span className="wo-link__badge">{messages.length}</span>
              )}
            </Button>
          </div>
        </div>

        {/* The report on the left, everything that is context about it in a
            rail on the right that stays put as the findings scroll. */}
        <div className="wo-grid">
          <div className="wo-main">
            <WorkOrderItems
              items={workOrder.items ?? []}
              parts={workOrder.parts ?? []}
              status={status}
              canWrite={canWrite}
              currencySymbol={this.currencySymbol()}
              busy={Boolean(acting)}
              openPanels={openPanels}
              onAddItem={() =>
                this.openItemForm({
                  item: null,
                  parentId: null,
                  isOption: false,
                })
              }
              onPickServices={this.openServicePicker}
              onAddSub={(parent) =>
                this.openItemForm({
                  item: null,
                  parent,
                  parentId: parent.id,
                  isOption: false,
                  defaultType: parent.type,
                })
              }
              onAddOption={(parent) =>
                this.openItemForm({
                  item: null,
                  parent,
                  parentId: parent.id,
                  isOption: true,
                  defaultType: ItemType.OPTIONAL,
                })
              }
              onEdit={(item) => this.openItemForm({ item })}
              onRemove={this.removeItem}
              onTogglePanel={this.togglePanel}
              onOpenThread={this.openThread}
              onUploadPhoto={this.uploadPhoto}
              onRemovePhoto={this.removePhoto}
            />
          </div>

          <div className="wo-side">
            {this.renderDelivery(workOrder, canWrite)}

            <div className="wo-card">
              <div className="wo-card__head">
                <h6 className="wo-card__title">
                  <i className="nc-icon nc-paper" />
                  {strings.mechanic_note}
                </h6>
              </div>
              <textarea
                className="form-control"
                rows="3"
                value={note}
                disabled={!noteEditable}
                placeholder={strings.mechanic_note_hint}
                onChange={(event) =>
                  this.setState({ note: event.target.value, noteDirty: true })
                }
              />
              {this.renderFeedback("note")}
              {noteEditable && (
                <div className="wo-item__tools">
                  <ButtonLoader
                    color="primary"
                    size="sm"
                    loading={savingNote}
                    onClick={this.saveNote}
                  >
                    {strings.save_note}
                  </ButtonLoader>
                </div>
              )}
            </div>

            {this.renderParts(workOrder, canWrite)}
            {this.renderPayment()}
            {this.renderActions(workOrder, canWrite)}
          </div>
        </div>

        <MessageDrawer
          isOpen={!!thread}
          threadKey={thread?.key}
          title={thread?.title}
          subtitle={thread?.subtitle}
          messages={thread?.messages ?? []}
          sending={Boolean(acting)}
          draft={drafts[thread?.key] || ""}
          onDraftChange={(value) => this.setDraft(thread.key, value)}
          onSend={this.sendThreadMessage}
          onClose={this.closeThread}
        />

        <ItemFormModal
          isOpen={dialog === "item"}
          item={itemDraft?.item}
          parent={itemDraft?.parent}
          parentId={itemDraft?.parentId}
          isOption={itemDraft?.isOption}
          defaultType={itemDraft?.defaultType}
          currencySymbol={this.currencySymbol()}
          saving={Boolean(acting)}
          error={dialogError}
          onSubmit={this.submitItem}
          onClose={this.closeDialog}
        />

        <ApproveWorkModal
          isOpen={dialog === "approve"}
          parts={workOrder.parts ?? []}
          saving={Boolean(acting)}
          error={dialogError}
          onSubmit={this.submitApproveWork}
          onClose={this.closeDialog}
        />

        <CollectionModal
          isOpen={dialog === "collection"}
          methods={this.state.deliveryMethods}
          arrivedBy={workOrder.arrivalMethodName}
          saving={Boolean(acting)}
          error={dialogError}
          onSubmit={this.submitCollection}
          onClose={this.closeDialog}
        />

        <ServicePickerModal
          isOpen={dialog === "pick_services"}
          services={this.state.branchServices}
          currencySymbol={this.currencySymbol()}
          saving={Boolean(acting)}
          error={dialogError}
          onSubmit={this.submitServicePick}
          onClose={this.closeDialog}
        />

        <ServicesCostModal
          isOpen={dialog === "services"}
          value={workOrder.servicesCost}
          currencySymbol={this.currencySymbol()}
          saving={Boolean(acting)}
          error={dialogError}
          onSubmit={this.submitServicesCost}
          onClose={this.closeDialog}
        />

        <AssigneeModal
          isOpen={dialog === "assignee"}
          workers={workers.filter((one) => one.role === "mechanic")}
          // The final quote only keeps whoever is already on the job — the
          // reporting mechanics aren't pre-checked, so the assignee is picked
          // deliberately.
          selected={
            assigneeCtx?.mode === "quote"
              ? (this.state.workOrder?.assignees ?? []).map((one) => one.id)
              : this.defaultAssigneeIds()
          }
          allowLater={assigneeCtx?.allowLater}
          saving={Boolean(acting)}
          onSubmit={this.submitAssignees}
          onClose={this.closeDialog}
        />

        <DeliveryModal
          isOpen={dialog === "delivery"}
          value={deliveryCtx?.mode === "change" ? workOrder.deliveryAt : null}
          schedule={schedule}
          saving={Boolean(acting)}
          error={dialogError}
          title={
            deliveryCtx?.mode === "change"
              ? strings.change_date
              : strings.send_final_quote
          }
          saveLabel={
            deliveryCtx?.mode === "change" ? strings.save : strings.next
          }
          onSubmit={this.submitDelivery}
          onClose={this.closeDialog}
        />

        <ConfirmModal
          isOpen={dialog === "confirm"}
          title={confirmCtx?.title}
          body={confirmCtx?.body}
          confirmLabel={confirmCtx?.confirmLabel}
          color={confirmCtx?.color}
          saving={Boolean(acting)}
          onConfirm={() => confirmCtx?.run()}
          onClose={this.closeDialog}
        />
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(WorkOrderManage);
